import React, { useCallback, useEffect, useState } from 'react';
import './NdcWhiteListSetup.scss';
import CommonHeader from '../../components/CommonHeader';
import { Button, Dropdown, Table } from 'react-bootstrap';
import { ReactSVG } from 'react-svg';
import threedotvertical from "../../assets/images/three-dots-vertical.svg";
import viewicon from "../../assets/images/view-icon.svg";
import editicon from "../../assets/images/edit-icon.svg";
import deleteicon from "../../assets/images/delete-icon.svg";
import useNdcWhitelistSetup from '../../Hooks/useNdcWhitelistSetup';
import Loading from '../../components/LoadingPage/Loading';
import moment from 'moment';
import AddAthenaOrderDialog from './AddAthenaOrderDialog/AddAthenaOrderDialog';
import AddEditNdcWhiteListDialog from './AddEditNdcWhiteListDialog/AddEditNdcWhiteListDialog';
import { DeleteConfModel } from '../../components/DeleteConfModel/deleteConfModel';
import { toast } from 'react-toastify';

const NdcWhiteListSetup: React.FC = () => {
    const [searchText, setSearchText] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [athenaOrderList, setAthenaOrderList] = useState<any[]>([]);
    const [selectedAthenaOrder, setSelectedAthenaOrder] = useState<Record<string, any> | null>(null);
    const [ndcWhiteList, setNdcWhiteList] = useState<any[]>([]);
    const [isAddOrderModalOpen, setIsAddOrderModalOpen] = useState<boolean>(false);
    const [isNdcDialogOpen, setIsNdcDialogOpen] = useState<boolean>(false);
    const [selectedNdcWhitelist, setSelectedNdcWhitelist] = useState<Record<string, any> | null>(null);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const [deleteNdcItem, setDeleteNdcItem] = useState<Record<string, any> | null>(null);

    const {
        getAthenaOrderListAPI,
        getAthenaOrderListLoading,
        getAthenaOrderNDCWhiteListAPI,
        getAthenaOrderNDCWhiteListLoading,
        deleteAthenaOrderNDCWhiteListAPI,
        deleteAthenaOrderNDCWhiteListLoading,
    } = useNdcWhitelistSetup();

    useEffect(() => {
        fetchAthenaOrders();
    }, [searchText, isActive]);

    const fetchAthenaOrders = useCallback(async () => {
        try {
            const res = await getAthenaOrderListAPI({ active: isActive, searchText });
            setAthenaOrderList(res || []);
        } catch (error) {
            console.error("Error while fetching athena order list: ", error);
        }
    }, [searchText, isActive]);

    const fetchNdcWhiteList = useCallback(async (orderId: string) => {
        if (!orderId) return;
        try {
            const res = await getAthenaOrderNDCWhiteListAPI({ active: true, athenaOrderMasterId: orderId });
            setNdcWhiteList(res || []);
        } catch (error) {
            console.error("Error while fetching ndc white list: ", error);
        }
    }, []);

    const handleOrderClick = (order: any) => {
        setSelectedAthenaOrder(order);
        fetchNdcWhiteList(order?.id);
    };

    const handleSearchChange = useCallback((value: string) => {
        if (value === searchText) return;
        setSearchText(value);
        setSelectedAthenaOrder(null);
        setNdcWhiteList([]);
    }, []);

    const handleActiveChange = useCallback((checked: boolean) => {
        setIsActive(checked);
        setSelectedAthenaOrder(null);
        setNdcWhiteList([]);
    }, []);

    const handleCloseAthenaOrderDialog = useCallback(() => {
        setIsAddOrderModalOpen(false);
        fetchAthenaOrders();
    }, []);

    const handleOpenNdcDialog = (selectedRecord?: Record<string, any>) => {
        setSelectedNdcWhitelist(selectedRecord || null);
        setIsNdcDialogOpen(true);
    }

    const handleCloseNdcDialog = useCallback((isRefresh = false) => {
        setIsNdcDialogOpen(false);
        setSelectedNdcWhitelist(null);
        if (isRefresh) {
            fetchNdcWhiteList(selectedAthenaOrder?.id);
        }
    }, [selectedAthenaOrder?.id]);

    const toggleDeleteModal = () => {
        setShowDeleteModal(!showDeleteModal);
    };

    const handleDeleteNdc = async () => {
        if (!deleteNdcItem?.id) return;
        try {
            const res = await deleteAthenaOrderNDCWhiteListAPI(deleteNdcItem.id);
            if (res?.success) {
                toast.success(res?.message || "NDC removed successfully from whitelist!");
                toggleDeleteModal();
                setDeleteNdcItem(null);
                fetchNdcWhiteList(selectedAthenaOrder?.id);
            }
        } catch (error) {
            console.error("Error while deleting ndc whitelist: ", error);
        }
    };

    const formatDate = (date: any) => {
        if (!date) return "-";
        return moment(date).format("MM/DD/YYYY");
    };

    const isLoading = getAthenaOrderListLoading || getAthenaOrderNDCWhiteListLoading || deleteAthenaOrderNDCWhiteListLoading;

    return (
        <div className='ndc-whitelist-setup'>
            {isLoading && <Loading />}

            <CommonHeader
                pageTitle="NDC White List Setup"
                searchValue={searchText}
                handleSearchChange={handleSearchChange}
                checkboxes={[
                    {
                        label: "Active",
                        checked: isActive,
                        onChange: (checked) => handleActiveChange(checked),
                    },
                ]}
            />

            <div className='ndc-whitelist-setup__data-section-wrapper'>
                <div className="ndc-whitelist-setup__data-section">
                    <div className="ndc-whitelist-setup__section-header">
                        <h4>Athena Order List</h4>
                        <Button variant="primary" size='sm' onClick={() => setIsAddOrderModalOpen(true)}>
                            <i className="bi bi-plus"></i>
                            Add Order
                        </Button>
                    </div>
                    <div className="ndc-whitelist-setup__table-wrapper">
                        <Table responsive hover={true}>
                            <thead>
                                <tr>
                                    <th>Order Type Id</th>
                                    <th>Name</th>
                                    <th>Created By</th>
                                    <th>Created Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {athenaOrderList?.length > 0 ?
                                    athenaOrderList?.map((order) => (
                                        <tr key={order?.id} className={selectedAthenaOrder?.id === order?.id ? "selected" : ""}>
                                            <td title={order.orderTypeId || ""}>
                                                <span className="clickable-text" onClick={() => handleOrderClick(order)}>{order.orderTypeId}</span>
                                            </td>
                                            <td title={order.name || ""}>{order.name || "-"}</td>
                                            <td title={order?.createdByUser?.userName || ""}>{order?.createdByUser?.userName || "-"}</td>
                                            <td title={formatDate(order?.createdOnUtc)}>{formatDate(order?.createdOnUtc)}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td colSpan={4} className="text-center">
                                                No records found
                                            </td>
                                        </tr>
                                    )
                                }
                            </tbody>
                        </Table>
                    </div>
                </div>

                {(selectedAthenaOrder && !getAthenaOrderNDCWhiteListLoading) && (
                    <div className="ndc-whitelist-setup__data-section">
                        <div className="ndc-whitelist-setup__section-header">
                            <h4>NDC White List</h4>
                            <Button variant="primary" size='sm' onClick={() => handleOpenNdcDialog()}>
                                <i className="bi bi-plus"></i>
                                Add NDC
                            </Button>
                        </div>
                        <div className="ndc-whitelist-setup__table-wrapper">
                            <Table responsive hover={true}>
                                <thead>
                                    <tr>
                                        <th>Order Type Id</th>
                                        <th>Name</th>
                                        <th>NDC Code</th>
                                        <th>Is Verified</th>
                                        <th>Created By</th>
                                        <th>Created Date</th>
                                        <th>Edited By</th>
                                        <th>Edited Date</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ndcWhiteList?.length > 0 ?
                                        ndcWhiteList?.map((item) => (
                                            <tr>
                                                <td title={item?.athenaOrderMaster?.orderTypeId || ""}>{item?.athenaOrderMaster?.orderTypeId || "-"}</td>
                                                <td title={item?.athenaOrderMaster?.name || ""}>{item?.athenaOrderMaster?.name || "-"}</td>
                                                <td title={item?.ndcCode || ""}>{item?.ndcCode || "-"}</td>
                                                <td title={item?.isVerified ? "Yes" : "No"}>{item?.isVerified ? "Yes" : "No"}</td>
                                                <td title={item?.createdByUser?.userName || ""}>{item?.createdByUser?.userName || "-"}</td>
                                                <td title={formatDate(item?.createdOnUtc)}>{formatDate(item?.createdOnUtc)}</td>
                                                <td title={item?.updatedByUser?.userName || ""}>{item?.updatedByUser?.userName || "-"}</td>
                                                <td title={formatDate(item?.updatedOnUtc)}>{formatDate(item?.updatedOnUtc)}</td>
                                                <td>
                                                    <Dropdown>
                                                        <Dropdown.Toggle variant="link" id="dropdown-basic">
                                                            <ReactSVG className="svg-box" wrapper="span" src={threedotvertical} />
                                                        </Dropdown.Toggle>
                                                        <Dropdown.Menu renderOnMount popperConfig={{ strategy: 'fixed' }}>
                                                            <Dropdown.Item onClick={() => handleOpenNdcDialog({ ...item, mode: 'view' })}>
                                                                <ReactSVG className="svg-box" wrapper="span" src={viewicon} />
                                                                View
                                                            </Dropdown.Item>
                                                            <Dropdown.Item onClick={() => handleOpenNdcDialog({ ...item, mode: 'edit' })}>
                                                                <ReactSVG className="svg-box" wrapper="span" src={editicon} />
                                                                Edit
                                                            </Dropdown.Item>
                                                            <Dropdown.Item onClick={() => {
                                                                setDeleteNdcItem(item);
                                                                toggleDeleteModal();
                                                            }}>
                                                                <ReactSVG className="svg-box" wrapper="span" src={deleteicon} />
                                                                Delete
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={9} className="text-center">
                                                    No records found
                                                </td>
                                            </tr>
                                        )
                                    }
                                </tbody>
                            </Table>
                        </div>
                    </div>
                )}

                {isAddOrderModalOpen && (
                    <AddAthenaOrderDialog isOpen={isAddOrderModalOpen} onClose={handleCloseAthenaOrderDialog} />
                )}

                {(isNdcDialogOpen && selectedAthenaOrder) && (
                    <AddEditNdcWhiteListDialog
                        isOpen={isNdcDialogOpen}
                        onClose={handleCloseNdcDialog}
                        selectedAthenaOrder={selectedAthenaOrder}
                        selectedNdcWhitelist={selectedNdcWhitelist}
                    />
                )}
            </div>

            <DeleteConfModel
                showModal={showDeleteModal}
                toggleModal={toggleDeleteModal}
                deleteAction={handleDeleteNdc}
                modelName="NDC from Whitelist"
                recordName={deleteNdcItem?.ndcCode || ""}
            />
        </div>
    )
}

export default NdcWhiteListSetup;